import type { JSX } from 'react'
import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { LogOut } from 'lucide-react'
import type { OrgCurrentDto, UpdateProfileRequest } from '@/lib/dto'
import { api, request } from '@/lib/api'
import { qk } from '@/lib/query-keys'
import { useSystemInfo } from '@/hooks/queries'
import { activeRole, useSessionStore } from '@/stores/session'
import { DEFAULT_TOAST_MS, useToastStore } from '@/stores/toasts'
import { useDraft } from '@/ui/hooks/useDraft'
import { Button } from '@/ui/primitives/Button'
import { Chip } from '@/ui/primitives/Chip'
import { PasswordField, TextField } from '@/ui/primitives/Field'
import { Panel, Surface } from '@/ui/primitives/Surface'
import { ErrorBlock } from '../shared/states'
import { fieldErrorsOf, problemText } from '../shared/problem'
import { TotpPanel } from './TotpPanel'

/**
 * Настройки: профиль, безопасность, лимиты организации, версии ядра.
 *
 * Экран сознательно не имеет «Сохранить всё» внизу: разделы независимы, и
 * кнопка, сохраняющая одновременно пароль и лимиты, — источник страха.
 * Каждое действие подтверждается там, где его сделали.
 */

const ROLE_LABEL: Record<string, string> = {
  OWNER: 'Владелец',
  ADMIN: 'Администратор',
  OPERATOR: 'Оператор',
  VIEWER: 'Наблюдатель'
}

export function SettingsScreen(): JSX.Element {
  const user = useSessionStore(state => state.user)
  const memberships = useSessionStore(state => state.memberships)
  const activeOrgId = useSessionStore(state => state.activeOrgId)
  const role = activeRole({ memberships, activeOrgId })

  return (
    <>
      {/* Заголовка секции нет намеренно: «Настройки» уже стоят в хедере каркаса,
          а действий на уровне экрана здесь нет — каждый раздел сохраняет себя сам. */}
      <ProfilePanel>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Chip tone="honey">{ROLE_LABEL[role ?? ''] ?? 'Роль неизвестна'}</Chip>
          <Chip>
            {memberships.find(item => item.orgId === activeOrgId)?.orgName ??
              'Организация не выбрана'}
          </Chip>
        </div>
      </ProfilePanel>

      <SecurityPanel totpEnabled={user?.totpEnabled === true} />
      <OrgPanel />
      <SystemPanel />
    </>
  )
}

// ─────────────────────────────── профиль ───────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Правка профиля (`PATCH /auth/me`).
 *
 * Кнопка «Сохранить» появляется только когда что-то изменилось: постоянно
 * висящая активная кнопка на форме из двух полей провоцирует «сохранить»
 * вхолостую, а сервер на пустое тело отвечает 400.
 *
 * Смена почты сбрасывает подтверждение адреса, но НЕ рвёт сессии (email не
 * участвует в авторизации), поэтому редиректа на логин здесь нет — в отличие от
 * смены пароля.
 *
 * Поля наполняет `useDraft`, а не `useEffect(() => setDisplayName(user…), […])`:
 * сессия приезжает после bootstrap'а и обновляется потом (смена организации,
 * повторный `/auth/me`), и эффект перетирал бы наполовину введённое имя.
 */
function ProfilePanel({ children }: { children: JSX.Element }): JSX.Element {
  const user = useSessionStore(state => state.user)
  const setSession = useSessionStore(state => state.setSession)
  const push = useToastStore(state => state.push)

  // Метка формы — id пользователя: сменился аккаунт, значит форма другая.
  const draft = useDraft(
    { displayName: user?.displayName ?? '', email: user?.email ?? '' },
    user?.id ?? null
  )
  const { displayName, email } = draft.value
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)

  const dirty =
    displayName.trim() !== (user?.displayName ?? '') ||
    email.trim().toLowerCase() !== (user?.email ?? '').toLowerCase()

  const save = useMutation({
    mutationFn: (dto: UpdateProfileRequest) => api.auth.updateMe(dto),
    onSuccess: me => {
      setSession(me)
      // Черновик снова синхронизируем с сервером: иначе введённое «Имя  » с
      // хвостовыми пробелами так и висело бы в поле после сохранения.
      draft.reset()
      setErrors({})
      setFormError(null)
      push({ message: 'Профиль сохранён', tone: 'success', timeoutMs: DEFAULT_TOAST_MS })
    },
    onError: error => {
      // Занятый адрес приходит как VALIDATION_FAILED с path='email' — ошибка
      // должна оказаться под полем, а не общим баннером наверху.
      const fields = fieldErrorsOf(error)
      if (Object.keys(fields).length > 0) setErrors(fields)
      setFormError(Object.keys(fields).length > 0 ? null : problemText(error))
    }
  })

  const validate = (): boolean => {
    const found: Record<string, string> = {}
    const name = displayName.trim()
    if (name === '') found.displayName = 'Имя не может быть пустым'
    else if (name.length > 64) found.displayName = 'Не длиннее 64 символов'
    if (email.trim() === '') found.email = 'Введите почту'
    else if (!EMAIL_RE.test(email.trim())) found.email = 'Похоже, это не адрес почты'
    setErrors(found)
    return Object.keys(found).length === 0
  }

  return (
    <Panel title="Профиль" note="кто вы в этой панели">
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={event => {
          event.preventDefault()
          setFormError(null)
          if (!validate()) return
          // Отправляем только изменённое: сервер пишет в аудит список полей, и
          // «сменил почту на ту же самую» там лишнее.
          const dto: UpdateProfileRequest = {}
          if (displayName.trim() !== user?.displayName) dto.displayName = displayName.trim()
          if (email.trim().toLowerCase() !== user?.email) dto.email = email.trim()
          save.mutate(dto)
        }}
      >
        {/* Ширина ограничена: поле ввода на 1600px читается как ошибка вёрстки, а
            строка длиннее ~70 символов всё равно неудобна глазу. */}
        <div className="grid max-w-160 gap-4 sm:grid-cols-2">
          <TextField
            label="Имя"
            value={displayName}
            required
            maxLength={64}
            autoComplete="name"
            error={errors.displayName ?? null}
            onChange={event => draft.patch({ displayName: event.target.value })}
          />
          <TextField
            label="E-mail"
            type="email"
            mono
            value={email}
            required
            autoComplete="email"
            hint="После смены адрес придётся подтвердить заново; сессии не закроются"
            error={errors.email ?? null}
            onChange={event => draft.patch({ email: event.target.value })}
          />
        </div>

        {formError === null ? null : (
          <p
            role="alert"
            className="max-w-160 rounded-sm border border-fail/40 bg-fail/12 px-3 py-2 text-base text-fail"
          >
            {formError}
          </p>
        )}

        {dirty ? (
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" variant="primary" loading={save.isPending}>
              Сохранить профиль
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                draft.reset()
                setErrors({})
                setFormError(null)
              }}
            >
              Отменить
            </Button>
          </div>
        ) : null}
      </form>
      {children}
    </Panel>
  )
}

// ───────────────────────────── безопасность ─────────────────────────────

function SecurityPanel({ totpEnabled }: { totpEnabled: boolean }): JSX.Element {
  const navigate = useNavigate()
  const push = useToastStore(state => state.push)
  const clearSession = useSessionStore(state => state.clear)

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)

  const leaveToLogin = (message: string): void => {
    push({ message, tone: 'success', timeoutMs: DEFAULT_TOAST_MS })
    clearSession()
    void navigate({ to: '/login' })
  }

  /**
   * Смена пароля идёт напрямую через `request`, а не через `api.*`: метода
   * `api.auth.changePassword` в клиенте нет, а эндпоинт `POST /auth/password`
   * в бэкенде есть. Править `lib/api.ts` в этой задаче нельзя — метод стоит
   * добавить туда отдельно.
   */
  const change = useMutation({
    mutationFn: (dto: { currentPassword: string; newPassword: string }) =>
      request<void>('/auth/password', { method: 'POST', body: dto }),
    // Сервер гасит все сессии и чистит cookie: остаться на странице нельзя.
    onSuccess: () => leaveToLogin('Пароль изменён — войдите заново'),
    onError: error => setFormError(problemText(error))
  })

  const logoutAll = useMutation({
    mutationFn: () => api.auth.logoutAll(),
    onSuccess: () => leaveToLogin('Все сессии закрыты'),
    onError: error =>
      push({ message: problemText(error), tone: 'danger', timeoutMs: DEFAULT_TOAST_MS })
  })

  const validate = (): boolean => {
    const found: Record<string, string> = {}
    if (current === '') found.current = 'Введите текущий пароль'
    // 12 символов — требование сервера (PasswordSchema), а не вкусовое.
    if (next.length < 12) found.next = 'Не короче 12 символов'
    if (next === current && next !== '') found.next = 'Новый пароль совпадает с текущим'
    if (repeat !== next) found.repeat = 'Пароли не совпадают'
    setErrors(found)
    return Object.keys(found).length === 0
  }

  return (
    <Panel title="Безопасность" note="пароль, второй фактор, чужие сессии">
      <form
        className="flex max-w-140 flex-col gap-4"
        noValidate
        onSubmit={event => {
          event.preventDefault()
          setFormError(null)
          if (validate()) change.mutate({ currentPassword: current, newPassword: next })
        }}
      >
        <PasswordField
          label="Текущий пароль"
          autoComplete="current-password"
          value={current}
          required
          error={errors.current ?? null}
          onBlur={() => {
            // Валидируем на blur только заполненное поле: ругаться на пустое,
            // через которое просто прошли табом, — это шум, а не помощь.
            if (current !== '') validate()
          }}
          onChange={event => setCurrent(event.target.value)}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <PasswordField
            label="Новый пароль"
            autoComplete="new-password"
            hint="От 12 символов — это требование сервера"
            value={next}
            required
            error={errors.next ?? null}
            onBlur={() => {
              if (next !== '') validate()
            }}
            onChange={event => setNext(event.target.value)}
          />
          <PasswordField
            label="Новый пароль ещё раз"
            autoComplete="new-password"
            value={repeat}
            required
            error={errors.repeat ?? null}
            onBlur={() => {
              if (repeat !== '') validate()
            }}
            onChange={event => setRepeat(event.target.value)}
          />
        </div>

        {formError === null ? null : (
          <p
            role="alert"
            className="rounded-sm border border-fail/40 bg-fail/12 px-3 py-2 text-base text-fail"
          >
            {formError}
          </p>
        )}

        <p className="text-2xs text-ink-muted">
          После смены пароля сервер закрывает все сессии — понадобится войти заново.
        </p>

        <Button type="submit" variant="primary" className="self-start" loading={change.isPending}>
          Сменить пароль
        </Button>
      </form>

      <div className="mt-6 flex flex-col gap-4">
        <TotpPanel enabled={totpEnabled} />

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line pt-4">
          <div className="min-w-0">
            <p className="text-base text-ink">Выйти на всех устройствах</p>
            <p className="mt-1 max-w-prose text-2xs text-ink-muted">
              Закрывает все сессии, включая эту. Нужно, если панель осталась открытой на чужом
              компьютере.
            </p>
          </div>
          <Button variant="danger" loading={logoutAll.isPending} onClick={() => logoutAll.mutate()}>
            <LogOut aria-hidden className="size-4" />
            Закрыть все сессии
          </Button>
        </div>
      </div>
    </Panel>
  )
}

// ───────────────────────────── организация ──────────────────────────────

function OrgPanel(): JSX.Element {
  // Хука для `/orgs/current` в `hooks/queries.ts` нет, а править его нельзя —
  // берём тот же клиент и тот же ключ кэша напрямую.
  const org = useQuery<OrgCurrentDto>({
    queryKey: qk.org.current,
    queryFn: () => api.org.current()
  })

  if (org.isPending) {
    return (
      <Panel title="Организация" note="план и лимиты">
        <div className="flex flex-col gap-3">
          {Array.from({ length: 3 }, (_unused, index) => (
            <Surface key={index} variant="flat" className="shimmer h-12" aria-hidden>
              <span className="sr-only">Загрузка</span>
            </Surface>
          ))}
        </div>
      </Panel>
    )
  }

  if (org.isError || org.data === undefined) {
    return (
      <Panel title="Организация" note="план и лимиты">
        <ErrorBlock
          error={org.error}
          onRetry={() => void org.refetch()}
          title="Лимиты организации не загрузились"
        />
      </Panel>
    )
  }

  const data = org.data

  return (
    <Panel title="Организация" note={`план: ${PLAN_LABEL[data.plan] ?? data.plan}`}>
      <p className="font-display text-md font-semibold text-ink">{data.name}</p>
      {/* Полосы шириной во всю панель на широком мониторе превращаются в
          волоски через полтора метра экрана — держим их в читаемой колонке. */}
      <div className="mt-4 flex max-w-160 flex-col gap-4">
        <Usage label="Боты" used={data.usage.bots} limit={data.limits.maxBots} />
        {/* Лимита на MC-аккаунты в модели нет (docs/05 §2 `Org.limits` — только
            maxBots, maxViewers, logRetentionDays). Пишем это прямо, а не рисуем
            пустое место после «из». */}
        <Usage label="Minecraft-аккаунты" used={data.usage.mcAccounts} limit={null} />
        <Usage
          label="Сессии камеры"
          used={data.usage.activeViewers}
          limit={data.limits.maxViewers}
        />
      </div>
      <p className="mt-4 text-2xs text-ink-muted">
        Логи хранятся {data.limits.logRetentionDays} дн., дальше чистятся автоматически. Профилей
        серверов: {data.usage.servers}, участников: {data.usage.members}.
      </p>
    </Panel>
  )
}

const PLAN_LABEL: Record<string, string> = {
  SELF_HOSTED: 'свой сервер',
  HOBBY: 'hobby',
  CLAN: 'clan',
  SERVER: 'server'
}

/**
 * Полоса «использовано из лимита». Число рядом обязательно: полоса показывает
 * пропорцию, но «14 из 16» — это то, по чему принимают решение.
 *
 * `limit === null` — «лимита нет вовсе». Раньше сюда приезжал `undefined`
 * (ключи лимитов на фронте не совпадали с серверными), и строка обрывалась на
 * «3 из » — выглядело как поломка загрузки, хотя данные пришли.
 */
function Usage({
  label,
  used,
  limit
}: {
  label: string
  used: number
  /** `null` — ограничения нет. */
  limit: number | null
}): JSX.Element {
  const capped = typeof limit === 'number' && Number.isFinite(limit) && limit > 0
  const pct = capped ? Math.min(100, Math.round((used / limit) * 100)) : 0
  const tight = capped && pct >= 90
  const text = capped ? `${used} из ${limit}` : `${used} — без ограничения`

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-base text-ink">{label}</span>
        <span className="font-mono text-xs text-ink-muted tabular-nums">
          {text}
          {tight ? ' — почти предел' : ''}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-pill bg-panel-3">
        <div
          role="progressbar"
          aria-label={`${label}: ${text}`}
          aria-valuenow={used}
          aria-valuemin={0}
          {...(capped ? { 'aria-valuemax': limit } : {})}
          className={tight ? 'h-full rounded-pill bg-warn' : 'h-full rounded-pill bg-honey'}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  )
}

// ─────────────────────────────── система ────────────────────────────────

function SystemPanel(): JSX.Element {
  const system = useSystemInfo()

  if (system.isPending) {
    return (
      <Panel title="Система" note="версии ядра и патчи">
        <div className="grid gap-3 sm:grid-cols-2">
          {Array.from({ length: 4 }, (_unused, index) => (
            <Surface key={index} variant="flat" className="shimmer h-12" aria-hidden>
              <span className="sr-only">Загрузка</span>
            </Surface>
          ))}
        </div>
      </Panel>
    )
  }

  if (system.isError || system.data === undefined) {
    return (
      <Panel title="Система" note="версии ядра и патчи">
        <ErrorBlock
          error={system.error}
          onRetry={() => void system.refetch()}
          title="Версии ядра не загрузились"
        />
      </Panel>
    )
  }

  const info = system.data

  return (
    <Panel title="Система" note="версии ядра и патчи">
      {/* Слой совместимости может не собраться — тогда версий просто нет, и
          вместо четырёх пустых плашек нужно сказать, что именно сломалось:
          ответ эндпоинта для того и придуман (docs/06 §7). */}
      {info.compat.ok ? null : (
        <p
          role="alert"
          className="mb-4 rounded-sm border border-warn/40 bg-warn/12 px-3 py-2 text-base text-warn"
        >
          Слой совместимости с Minecraft {info.compat.targetVersion} не собран, боты на этой версии
          не подключатся.
          {info.compat.error === undefined ? '' : ` Причина: ${info.compat.error}`}
        </p>
      )}

      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Version label="Панель" value={info.appVersion} />
        <Version label="mineflayer" value={info.mineflayer} />
        <Version label="minecraft-data" value={info.minecraftData} />
        <Version label="prismarine-chunk" value={info.prismarineChunk} />
      </dl>

      <div className="mt-5 flex flex-col gap-2 border-t border-line pt-4">
        <p className="text-xs font-medium text-ink-muted">Поддерживаемые версии Minecraft</p>
        <div className="flex flex-wrap gap-1.5">
          {info.supportedVersions.length === 0 ? (
            <span className="text-base text-ink-muted">Ядро не сообщило ни одной версии</span>
          ) : (
            info.supportedVersions.map(version => (
              <Chip key={version} mono tone="honey">
                {version}
              </Chip>
            ))
          )}
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-2 border-t border-line pt-4">
        <p className="text-xs font-medium text-ink-muted">Применённые патчи протокола</p>
        {info.patches.length === 0 ? (
          <span className="text-base text-ink-muted">Патчей нет — ядро работает на стоке</span>
        ) : (
          <ul className="flex flex-col gap-1">
            {info.patches.map(patch => (
              <li key={patch} className="font-mono text-2xs text-ink-muted">
                {patch}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Panel>
  )
}

/** `null` приходит, когда ядро не собрано: пустая ячейка выглядела бы как баг вёрстки. */
function Version({ label, value }: { label: string; value: string | null }): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 rounded-sm border border-line bg-panel-2 px-3 py-2">
      <dt className="truncate text-2xs text-ink-muted">{label}</dt>
      <dd className="truncate font-mono text-base text-ink tabular-nums">
        {value ?? <span className="text-ink-faint">не сообщено</span>}
      </dd>
    </div>
  )
}
